import { motion } from "framer-motion";
import { LayoutDashboard, Activity, MessagesSquare, CheckCircle2, Package, Workflow, Megaphone, BarChart3 } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const pillarIcons = [LayoutDashboard, Activity, MessagesSquare];
const menuIcons = [LayoutDashboard, Package, Workflow, Megaphone, BarChart3];

export default function Environment() {
  const { t } = useLanguage();
  const pillars = t("environment.pillars").map((p, i) => ({ ...p, icon: pillarIcons[i] }));
  const menuItems = t("environment.menu").map((label, i) => ({ label, icon: menuIcons[i] }));
  const activity = t("environment.activity");
  return (
    <section id="environment" className="py-32 border-t border-white/10" data-testid="environment-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] mb-4" data-testid="environment-eyebrow">
              {t("environment.eyebrow")}
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="environment-heading">
              {t("environment.heading")}
            </h2>
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed mt-6 max-w-lg">
              {t("environment.copy")}
            </p>

            <div className="mt-12 flex flex-col gap-8">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex gap-5"
                  data-testid={`environment-pillar-${i}`}
                >
                  <div className="shrink-0 w-11 h-11 border border-white/10 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-[#FFB800]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-syne font-bold text-lg">{p.title}</h3>
                    <p className="text-neutral-500 text-sm leading-relaxed mt-1.5 max-w-md">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="border border-white/10 bg-black/50 backdrop-blur-md overflow-hidden" data-testid="environment-dashboard">
              <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A00]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FFB800]" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                <span className="ml-3 text-xs tracking-widest uppercase text-neutral-500">
                  {t("environment.dashboard")}
                </span>
              </div>

              <div className="grid grid-cols-12">
                <div className="col-span-2 sm:col-span-3 border-r border-white/10 p-2 sm:p-4">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    {menuItems.map((item, i) => (
                      <span
                        key={item.label}
                        className={`flex items-center justify-center sm:justify-start gap-2 text-xs px-1.5 sm:px-3 py-2 ${
                          i === 0 ? "bg-[#FF5A00]/10 text-[#FF5A00] border border-[#FF5A00]/30" : "text-neutral-500"
                        }`}
                        title={item.label}
                        data-testid={`env-menu-${item.label.toLowerCase()}`}
                      >
                        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                        <span className="hidden sm:inline">{item.label}</span>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="col-span-10 sm:col-span-9 p-5 md:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <p className="font-syne font-bold text-sm">{t("environment.project")}</p>
                    <span className="text-xs font-mono text-[#FFB800]" data-testid="env-progress-value">68%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 overflow-hidden mb-8" data-testid="env-progress-bar">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "68%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full bg-[#FF5A00]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-8">
                    <div className="border border-white/10 p-4">
                      <p className="font-syne font-bold text-2xl text-[#FF5A00]">3</p>
                      <p className="text-[11px] text-neutral-500 mt-1">{t("environment.tile1")}</p>
                    </div>
                    <div className="border border-white/10 p-4">
                      <p className="font-syne font-bold text-2xl text-[#FFB800]">30d</p>
                      <p className="text-[11px] text-neutral-500 mt-1">{t("environment.tile2")}</p>
                    </div>
                  </div>

                  <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-3">{t("environment.recentActivity")}</p>
                  <div className="flex flex-col">
                    {activity.map((a) => (
                      <div key={a.text} className="flex items-center justify-between py-2.5 border-t border-white/5">
                        <span className="flex items-center gap-2 text-xs text-neutral-400">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#FF5A00]" />
                          {a.text}
                        </span>
                        <span className="text-[10px] text-neutral-600 font-mono">{a.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-neutral-600 mt-4 font-mono" data-testid="environment-caption">
              {t("environment.caption")}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
